/**
 * Процедуры `.cspr` в Библиотеке.
 *
 * Файл процедуры — обычный файл Библиотеки (v3 `/files`) с расширением `cspr`: синхронизируется
 * и версионируется как любой другой файл. cspr-server держит его рабочую копию для realtime-
 * редактирования под детерминированным ID `v3f` + числовой ID файла v3 (с ведущими нулями,
 * чтобы проходить общий формат ID файлов cspr-server).
 */

export const CSPR_EXTENSION = "cspr"

const LIBRARY_FILE_ID_PREFIX = "v3f"
const LIBRARY_FILE_ID_RE = /^v3f(\d{8,30})$/

export const isCsprExtension = (ext: string | null | undefined): boolean =>
  (ext ?? "").trim().replace(/^\./, "").toLowerCase() === CSPR_EXTENSION

/** ID файла v3 → ID рабочей копии на cspr-server */
export const libraryFileId = (v3FileId: number): string =>
  `${LIBRARY_FILE_ID_PREFIX}${String(Math.trunc(v3FileId)).padStart(8, "0")}`

/** ID рабочей копии cspr-server → ID файла v3; null — файл не из Библиотеки */
export const parseLibraryFileId = (fileId: string | null | undefined): number | null => {
  if (!fileId) return null
  const m = LIBRARY_FILE_ID_RE.exec(fileId)
  if (!m) return null
  const id = parseInt(m[1], 10)
  return Number.isSafeInteger(id) && id > 0 ? id : null
}

export const isLibraryFileId = (fileId: string | null | undefined): boolean =>
  parseLibraryFileId(fileId) !== null

/** Имя файла в Библиотеке для процедуры: `<название>.cspr` (недопустимые для имён символы заменяются) */
export const csprFileName = (title: string): string => {
  const base = title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, " ").replace(/\s+/g, " ").trim() || "Процедура"
  return `${base.slice(0, 180)}.${CSPR_EXTENSION}`
}

const CSPR_SUFFIX_RE = /\.cspr$/i

/** Название процедуры из имени файла: в интерфейсе Библиотеки расширение `.cspr` не показываем */
export const csprTitleFromFileName = (fileName: string): string =>
  fileName.replace(CSPR_SUFFIX_RE, "").trim() || fileName

/** Имя для скачивания процедуры: название без расширения → `<название>.cspr` */
export const csprDownloadName = (name: string): string =>
  CSPR_SUFFIX_RE.test(name) ? name : `${name}.${CSPR_EXTENSION}`

/** Ссылка на процедуру из Библиотеки (`libraryFolder` — для крошек и «Назад») */
export const libraryProcedureHref = (
  v3FileId: number,
  opts: { folderId?: number | null; edit?: boolean; preview?: { assetId: number; version?: number | null } } = {},
): string => {
  const params = new URLSearchParams()
  if (opts.edit) params.set("edit", "true")
  params.set("libraryFolder", opts.folderId != null ? String(opts.folderId) : "root")
  if (opts.preview) {
    params.set("previewAsset", String(opts.preview.assetId))
    if (opts.preview.version != null) params.set("previewVersion", String(opts.preview.version))
  }
  return `/cspr/${libraryFileId(v3FileId)}?${params.toString()}`
}
